import {DestroyRef, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {AnyAbility} from '@casl/ability';
import {unpackRules} from '@casl/ability/extra';
import {AbilityServiceSignal} from '@casl/angular';
import {load as loadFingerprintJS} from '@fingerprintjs/fingerprintjs';
import {mapResponse} from '@ngrx/operators';
import {signalStore, withComputed, withHooks, withState} from '@ngrx/signals';
import {Dispatcher, Events, on, withEventHandlers} from '@ngrx/signals/events';
import {withGlitchTracking, withStorageSync, withTrackedReducer} from '@oleksandroleniuk/ngrx-toolkit';
import {BaseProductConfig, PRODUCT_CONFIG_TOKEN} from '@scalified-monorepo/ng-shared';
import {HttpError} from '@scalified-monorepo/ng-shared/errors';
import {ClientIdUpdatedEvent, withDevTools} from '@scalified-monorepo/ng-shared/ngrx';
import {GA_EVENTS} from '@scalified-monorepo/shared';
import {IdentityProjection} from '@scalified-monorepo/shared/domain';
import {Angulartics2} from 'angulartics2';
import {catchError, exhaustMap, filter, from, map, of, retry, switchMap, tap, timer} from 'rxjs';
import {AuthHttpService} from '../services/auth-http.service';
import {TokenRefreshQueue} from '../services/token-refresh-queue.service';
import {guestIdentity, isAuthenticatedIdentity} from '../utils/identity.utils';
import {SetIdentityDirect} from './auth.debug.events';
import {AuthEvents} from './auth.events';

const stateName = 'authArea';

interface IAuthState {
  identity: IdentityProjection | null;
  supervisedIdentity: IdentityProjection | null;
  rememberMe: boolean;
}

const initialState: IAuthState = {
  identity: null,
  supervisedIdentity: null,
  rememberMe: false,
};

export const AuthStore = signalStore(
  {providedIn: 'root'},
  withDevTools(stateName, withGlitchTracking()),
  withState(initialState),
  withStorageSync({
    key: 'authArea',
    select: ({identity, rememberMe}) => {
      const email = rememberMe && isAuthenticatedIdentity(identity) ? identity?.email : undefined;
      return email ? {identity: {email}, rememberMe} : {rememberMe};
    },
  }),
  withEventHandlers(
    (
      state,
      events = inject(Events),
      authHttpService = inject(AuthHttpService),

      activatedRoute = inject(ActivatedRoute),
      angulartics2 = inject(Angulartics2),
      router = inject(Router),
      productConfig: BaseProductConfig = inject(PRODUCT_CONFIG_TOKEN),
      abilityService = inject<AbilityServiceSignal<AnyAbility>>(AbilityServiceSignal),
      tokenRefreshQueue = inject(TokenRefreshQueue)
    ) => ({
      getIdentity$: events.on(AuthEvents.GetIdentity).pipe(
        exhaustMap(() =>
          authHttpService.getIdentity().pipe(
            mapResponse({
              next: (identity: IdentityProjection) => AuthEvents.GetIdentitySuccess({identity}),
              error: error => {
                console.error('[AuthEffects] GetIdentity: HTTP error', error);
              },
            })
          )
        )
      ),
      updateAbility$: events
        .on(AuthEvents.GetIdentitySuccess)
        .pipe(
          tap(({payload: {identity}}) =>
            abilityService.update(unpackRules(identity.accounts.flatMap(account => account.rules ?? [])))
          )
        ),
      redirectOnAuth$: events.on(AuthEvents.Authenticated).pipe(
        tap(() => {
          const url = router.url.split('?')[0];
          if (productConfig.redirectOnAuthRoutes.some(x => url?.includes(x) || url === x)) {
            const redirectUrl =
              activatedRoute.snapshot.queryParams['redirectUrl'] || productConfig.initialProtectedRoute;
            console.info(`[AuthEffects] Authenticated: currentUrl=${url}, redirectUrl=${redirectUrl}`);
            router.navigateByUrl(redirectUrl);
          }
        })
      ),
      getIdentityOnAuth$: events.on(AuthEvents.Authenticated).pipe(map(() => AuthEvents.GetIdentity())),
      refreshToken$: events.on(AuthEvents.UnauthorizedError, AuthEvents.RefreshToken).pipe(
        exhaustMap(() => {
          const identity = state.identity();
          if (!isAuthenticatedIdentity(identity)) {
            return of(AuthEvents.Logout({autoRedirect: true}));
          }
          return authHttpService.refreshToken().pipe(
            tap(() => tokenRefreshQueue.completeRefresh()),
            map(response => AuthEvents.TokenRefreshed({email: response.email})),
            catchError((err: HttpError) => {
              tokenRefreshQueue.failRefresh();
              return of(AuthEvents.Logout({autoRedirect: true}));
            })
          );
        })
      ),
      tokenRefreshed$: events.on(AuthEvents.TokenRefreshed).pipe(map(() => AuthEvents.GetIdentity())),
      logout$: events.on(AuthEvents.Logout).pipe(
        exhaustMap(({payload: {autoRedirect}}) =>
          authHttpService.logout().pipe(
            catchError(() => of(null)),
            tap(() => {
              tokenRefreshQueue.clear();
              const currentUrl = router.url;
              const queryParams =
                autoRedirect && !currentUrl.includes('/login') ? {redirectUrl: currentUrl} : undefined;
              router.navigate(['/login'], {queryParams});
            })
          )
        )
      ),
      loginAnalytics$: events.on(AuthEvents.Authenticated).pipe(
        tap(({payload: {email}}) => {
          if (email) {
            angulartics2.setUsername.next(email);
            if (window['clarity']) {
              window['clarity']('identify', email);
            }
          }
        }),
        tap(({payload: {provider}}) => angulartics2.eventTrack.next(GA_EVENTS.app.login(provider))),
        tap(({payload: {provider, oauthSignup}}) => {
          if (oauthSignup && provider) {
            angulartics2.eventTrack.next(GA_EVENTS.app.userVerified(provider));
          }
        })
      ),
      enterSuperviseMode$: events.on(AuthEvents.SupervisedUserUpdated).pipe(
        filter(({payload}) => !!payload),
        tap(() => router.navigate([productConfig.initialProtectedRoute]))
      ),
      exitSuperviseMode$: events.on(AuthEvents.SupervisedUserUpdated).pipe(
        filter(({payload}) => !payload),
        tap(() => router.navigate(['/app-console/users']))
      ),
    })
  ),
  withTrackedReducer(
    on(AuthEvents.GetIdentitySuccess, ({payload: {identity}}) => ({identity})),
    on(AuthEvents.Logout, () => ({
      identity: guestIdentity(),
      supervisedIdentity: null,
    })),

    on(AuthEvents.Authenticated, ({payload: {email, rememberMe}}) => ({
      identity: new IdentityProjection({email}),
      rememberMe: rememberMe ?? false,
    })),
    on(SetIdentityDirect, ({payload: {identity}}) => ({identity})),

    on(AuthEvents.SupervisedUserUpdated, ({payload: {payload: supervisedIdentity}}) => ({supervisedIdentity}))
  ),
  withComputed(({identity, supervisedIdentity}) => ({
    activeAccount: () => identity()?.accounts?.[0] ?? null,
    supervisedAccount: () => supervisedIdentity()?.accounts?.[0] ?? null,
  })),
  withHooks({
    onInit() {
      const destroyRef = inject(DestroyRef);
      const dispatcher = inject(Dispatcher);

      timer(1000)
        .pipe(
          switchMap(() =>
            from(
              loadFingerprintJS()
                .then(fp => fp.get())
                .then(result => result.visitorId)
            ).pipe(
              // The FingerprintJS CDN can be blocked (adblock) or race the
              // user: retry with exponential backoff (2s/4s/8s) before giving
              // up. Final failure is swallowed — the auth UI guards on an
              // empty clientId instead of surfacing an error.
              retry({count: 3, delay: (_error, retryCount) => timer(1000 * 2 ** retryCount)}),
              catchError(() => of(null))
            )
          ),
          takeUntilDestroyed(destroyRef)
        )
        .subscribe(visitorId => {
          if (visitorId) {
            dispatcher.dispatch(ClientIdUpdatedEvent({clientId: visitorId}));
          }
        });
    },
  })
);
